import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, InfoWindowF, MarkerF, useLoadScript } from "@react-google-maps/api";
import { Box, CircularProgress, Typography } from "@mui/material";
import type { ShipmentStatus } from "../../types/DB2";

export type MapShipment = {
  id: string;
  status: ShipmentStatus;
  label: string;
  lat?: number;
  lng?: number;
  iconUrl?: string;
  addressSummary?: string;
  detailLines?: string[];
  subtitle?: string;
  orders?: Array<{
    orderId: string;
    itemCount: number;
    destinations: string[];
  }>;
};

type ShipmentsMapProps = {
  shipments: MapShipment[];
  selectedShipmentId?: string | null;
  onSelect?: (shipmentId: string | null) => void;
  isLoading?: boolean;
  mapStyles?: google.maps.MapTypeStyle[];
  initialZoom?: number;
};

const MAP_CONTAINER_STYLE: React.CSSProperties = {
  width: "100%",
  height: "100%",
};

const FALLBACK_CENTER = { lat: 39.8097343, lng: -98.5556199 }; // approximate geographic center of continental US

const statusIcon: Partial<Record<ShipmentStatus, string>> = {
  created: "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png",
  dispatched: "https://maps.google.com/mapfiles/ms/icons/ltblue-dot.png",
  inTransit: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
  delayed: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
  arrived: "https://maps.google.com/mapfiles/ms/icons/purple-dot.png",
  completed: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
};

const computeCenter = (shipments: MapShipment[]) => {
  const points = shipments.filter(
    (s) => typeof s.lat === "number" && typeof s.lng === "number"
  );
  if (!points.length) {
    return FALLBACK_CENTER;
  }
  const { latSum, lngSum } = points.reduce(
    (acc, curr) => ({
      latSum: acc.latSum + (curr.lat ?? 0),
      lngSum: acc.lngSum + (curr.lng ?? 0),
    }),
    { latSum: 0, lngSum: 0 }
  );
  return {
    lat: latSum / points.length,
    lng: lngSum / points.length,
  };
};

const buildBounds = (shipments: MapShipment[]): google.maps.LatLngBounds | null => {
  const googleInstance = (window as any).google as typeof google | undefined;
  if (!googleInstance?.maps?.LatLngBounds) return null;
  const bounds = new googleInstance.maps.LatLngBounds();
  let added = false;
  shipments.forEach((shipment) => {
    if (typeof shipment.lat === "number" && typeof shipment.lng === "number") {
      bounds.extend({ lat: shipment.lat, lng: shipment.lng });
      added = true;
    }
  });

  return added ? bounds : null;
};

const ShipmentsMap: React.FC<ShipmentsMapProps> = ({
  shipments,
  selectedShipmentId,
  onSelect,
  isLoading = false,
  mapStyles,
  initialZoom,
}) => {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const [activeInfoId, setActiveInfoId] = useState<string | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const readyShipments = useMemo(
    () => shipments.filter((s) => typeof s.lat === "number" && typeof s.lng === "number"),
    [shipments]
  );
  const center = useMemo(() => computeCenter(readyShipments), [readyShipments]);

  const { isLoaded, loadError } = useLoadScript({
    id: "admin-dashboard-map",
    googleMapsApiKey: apiKey ?? "",
    version: "weekly",
  });

  useEffect(() => {
    if (!selectedShipmentId) {
      setActiveInfoId(null);
      return;
    }
    setActiveInfoId(selectedShipmentId);
  }, [selectedShipmentId]);

  const fitMapToShipments = useCallback(
    (map: google.maps.Map | null) => {
      if (!map) return;
      if (typeof initialZoom === "number") {
        map.setCenter(center);
        map.setZoom(initialZoom);
        return;
      }
      const bounds = buildBounds(readyShipments);
      if (bounds) {
        map.fitBounds(bounds, 80);
      } else {
        map.setCenter(center);
        map.setZoom(4);
      }
    },
    [readyShipments, center, initialZoom]
  );

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    fitMapToShipments(mapRef.current);
  }, [isLoaded, readyShipments, center, fitMapToShipments]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    mapRef.current.setOptions({ styles: mapStyles ?? [] });
    console.log("[ShipmentsMap] applied map styles", {
      stylesLength: mapStyles?.length ?? 0,
    });
  }, [isLoaded, mapStyles]);

  const handleMarkerClick = useCallback(
    (shipmentId: string) => {
      setActiveInfoId(shipmentId);
      onSelect?.(shipmentId);
    },
    [onSelect]
  );

  const handleCloseInfo = useCallback(() => {
    setActiveInfoId(null);
    onSelect?.(null);
  }, [onSelect]);

  const handleOnLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      fitMapToShipments(map);
      map.setOptions({ styles: mapStyles ?? [] });
      console.log("[ShipmentsMap] map loaded", {
        appliedStyles: mapStyles?.length ?? 0,
        shipments: readyShipments.length,
      });
    },
    [fitMapToShipments, mapStyles, readyShipments.length]
  );

  const mapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      gestureHandling: "greedy",
    }),
    []
  );

  const resolveMarkerIcon = useCallback(
    (shipment: MapShipment) => shipment.iconUrl || statusIcon[shipment.status] || statusIcon.created,
    []
  );

  if (!apiKey) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          p: 3,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Google Maps API key not configured. Add <code>VITE_GOOGLE_MAPS_API_KEY</code> to continue.
        </Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          p: 3,
        }}
      >
        <Typography variant="body1" color="error">
          Failed to load Google Maps. Please check your API key and network connection.
        </Typography>
      </Box>
    );
  }

  if (!isLoaded || isLoading) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!readyShipments.length) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          p: 3,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No shipments with location data for the selected date.
        </Typography>
      </Box>
    );
  }

  return (
    <GoogleMap
      onLoad={handleOnLoad}
      mapContainerStyle={MAP_CONTAINER_STYLE}
      center={center}
      zoom={4}
      options={mapOptions}
    >
      {readyShipments.map((shipment) => (
        <MarkerF
          key={shipment.id}
          position={{ lat: shipment.lat as number, lng: shipment.lng as number }}
          onClick={() => handleMarkerClick(shipment.id)}
          icon={resolveMarkerIcon(shipment)}
          title={shipment.label}
        >
          {activeInfoId === shipment.id && (
            <InfoWindowF
              onCloseClick={handleCloseInfo}
              position={{
                lat: shipment.lat as number,
                lng: shipment.lng as number,
              }}
            >
              <Box sx={{ minWidth: 220 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {shipment.label}
                </Typography>
                {shipment.subtitle && (
                  <Typography variant="body2" color="text.secondary">
                    {shipment.subtitle}
                  </Typography>
                )}
                {shipment.addressSummary && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {shipment.addressSummary}
                  </Typography>
                )}
                {shipment.detailLines?.map((line, idx) => (
                  <Typography key={idx} variant="body2" color="text.secondary">
                    {line}
                  </Typography>
                ))}
                {shipment.orders?.length ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Orders:
                    </Typography>
                    {shipment.orders.slice(0, 3).map((order) => (
                      <Typography
                        key={order.orderId}
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        #{order.orderId} • {order.itemCount} item
                        {order.itemCount === 1 ? "" : "s"} •{" "}
                        {order.destinations.length
                          ? order.destinations.join(", ")
                          : "No destinations"}
                      </Typography>
                    ))}
                    {shipment.orders.length > 3 && (
                      <Typography variant="caption" color="text.secondary">
                        +{shipment.orders.length - 3} more…
                      </Typography>
                    )}
                  </Box>
                ) : null}
              </Box>
            </InfoWindowF>
          )}
        </MarkerF>
      ))}
    </GoogleMap>
  );
};

export default ShipmentsMap;
